import { useEffect, useState } from "react";
import { t } from "../i18n";

interface Props {
  dadName: string;
  styleLabel: string;
  /** Kept for API compatibility with App.tsx. The widget is always embedded in
   *  a product page now; the old standalone loading extras were removed. */
  widgetMode?: boolean;
}

// Fal generation typically completes in 10–18s. We animate to 95% over this
// duration; after that we creep 95→99 slowly so the bar never visually
// "freezes" if the Fal queue takes 30-60s. The screen unmounts when the
// parent's poll detects "ready", giving the "97% then poof, preview" moment.
const EXPECTED_DURATION_MS = 14000;

export function LoadingStep({ dadName, styleLabel, widgetMode }: Props) {
  const [pct, setPct] = useState(0);
  void styleLabel;
  void widgetMode;

  // Two-phase progress:
  //   Phase 1 (0–14s):  0 → 95%  (steady rise)
  //   Phase 2 (14s+):  95 → 99%  (asymptotic creep, never reaches 100% until ready)
  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      let ratio: number;
      if (elapsed < EXPECTED_DURATION_MS) {
        ratio = (elapsed / EXPECTED_DURATION_MS) * 0.95;
      } else {
        const overflow = (elapsed - EXPECTED_DURATION_MS) / 1000;
        // +0.04 over ~120s: 95% at 14s → 97% at 74s → 99% at 134s
        ratio = 0.95 + Math.min(0.04, overflow / 3000);
      }
      setPct(Math.floor(ratio * 100));
    }, 120);
    return () => clearInterval(tick);
  }, []);

  // Stage-aware headline. Each stage uses an i18n key so the JA register
  // replaces EN at render time when window.InujoyLocale === "ja".
  const heroName = dadName?.trim() || t("loading.hero_fallback");
  const headline =
    pct < 20
      ? t("loading.stage_1", { name: heroName })
      : pct < 50
      ? t("loading.stage_2", { name: heroName })
      : pct < 80
      ? t("loading.stage_3")
      : pct < 95
      ? t("loading.stage_4")
      : t("loading.stage_5");

  return (
    <div className="card loading-card">
      <div className="loading-top">
        <div className="brand-pulse" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
          </svg>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="progress-meta">{t("loading.percent", { pct })}</div>
      </div>

      <h2 className="loading-headline">{headline}</h2>
    </div>
  );
}
